import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import {IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AgyleToggleButtons from '../../../../../components/ui/buttons/agyleToggleButtons';
import SearchIcon from '@mui/icons-material/Search';
import {AgyleGlobalSearchIcon, AgyleMapSearchIcon} from '../../../../../components/icons/agyle';
import {wardPatients} from '../../../../../store/reducers/ward';
import _ from 'lodash';
import {setFilter, setWardSearchPatient} from '../../../../../store/reducers/wardMap';
import GlobalSearchModal from '../../../../../components/search/globalSearchModal';
import API from '../../../../../app/api';
import {setSnackbar} from '../../../../../store/reducers/globals';
import AgyleTextFieldWithHistory from '../../../../../components/ui/fields/agyleTextFieldWithHistory';
import {setLoading as setLoadingGlobal} from '../../../../../store/reducers/patient';
import { PATIENT_TAGS } from '../../../../../util/patientUtil';

const SEARCH_TYPE = [
    {value: 'map', label: <AgyleMapSearchIcon/>},
    {value: 'global', label: <AgyleGlobalSearchIcon/>},
];

const useStyles = makeStyles((theme) => ({
    menu: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        marginLeft: 10,
    },
    toggleRoot: {
        color: theme.palette.mode === 'dark' ? 'black' : '#f3f3f4',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#bfc2c7',
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
    },
    toggleLabel: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#f3f3f4',
        height: '24px!important',
    },
    toggleSelect: {
        color: theme.palette.mode === 'dark' ? 'black !important' : '#ffffff !important',
        fontWeight: theme.palette.mode === 'dark' ? 400 : 600,
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff !important' : '#626466 !important',
    },
    searchText: {
        height: 14,
    },
}));

const WardPanelSearch = ({toggle, size, onlyGlobal, bedManagement, appointments, maxResults = 3, disableMore = false, autoWidth = false}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Search component states
    const [searchType, setSearchType] = useState(onlyGlobal ? 'global' : 'map');
    const [search, setSearch] = useState('');

    // Global
    const [anchorEl, setAnchorEl] = useState(null);
    const [globalPatients, setGlobalPatients] = useState([]);

    // Selectors
    const wardPatientsObject = useSelector(wardPatients);

    // only ward/map search
    useEffect(() => {
        if (searchType === 'map') {
            if (search && !_.isEmpty(search) && wardPatientsObject) {
                const filteredUsers = wardPatientsObject.filter((user) =>
                    search
                        .toLowerCase()
                        .split(' ')
                        .filter((word) => word.trim() !== '')
                        .some((word) =>
                            ['patient.familyName', 'patient.firstName', 'patient.hospitalNumber'].some(
                                (field) => _.get(user, field, '')?.toLowerCase()?.includes(word),
                            ),
                        ),
                );
                dispatch(setWardSearchPatient(filteredUsers));
            } else {
                dispatch(setWardSearchPatient(null));
            }
        }
    }, [searchType, search, wardPatientsObject]);

    const handleSearch = (e) => {
        const {value} = e.target;
        setSearch(value);
        dispatch(setFilter({searchText: value}));
        dispatch(setWardSearchPatient(null));
    };

    const handleChangeType = (val) => {
        if (val) {
            setSearchType(val);
            setSearch('');
            dispatch(setFilter({searchText: ''}));
            dispatch(setWardSearchPatient(null));
        }
    };

    const handleSearchGlobal = (e, forceVal = null) => {
        if (searchType === 'global' && (e?.key === 'Enter' || forceVal)) {
            if (!_.isEmpty(search) || !_.isEmpty(forceVal)) {
                const options = {
                    resourceUri: '/patients',
                    text: forceVal || search,
                    page: 1,
                    pageSize: 100,
                    options: {},
                };

                dispatch(setLoadingGlobal(true));

                API.getSearchList(options)
                    .then(res => {
                        if (res.status === 200) {
                            const results = _.filter(res.data.collection['__data'], p => !_.isNull(p) && appointments ? !p?.tags?.includes(PATIENT_TAGS.TEMPORARY) : true);
                            setGlobalPatients(results);
                            const target = document.getElementById('global-search-field');
                            setAnchorEl(target);

                            if (_.isEmpty(results)) {
                                dispatch(setSnackbar({type: 'info', message: t('No matching patients found.')}));
                            }
                        } else if (res.status === 204) {
                            setGlobalPatients([]);
                            dispatch(setSnackbar({type: 'info', message: t('No matching patients found.')}));
                        } else if (res.status !== 200 && res.status !== 204) {
                            console.debug('API.getPatientsList request status : ' + res.status);
                            dispatch(setSnackbar({type: 'info', message: t('No patients found in the database')}));
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        setGlobalPatients([]);
                        dispatch(setSnackbar({
                            type: 'error',
                            message: t('An error occurred while searching for patients.'),
                        }));
                    })
                    .finally(() => dispatch(setLoadingGlobal(false)));
            }
        }
    };

    const handleCloseSearchModal = () => {
        setAnchorEl(null);
        setTimeout(() => {
            setGlobalPatients([]);
        }, 300);
    };

    return (
        <div className={classes.menu}>
            <AgyleTextFieldWithHistory
                placeholder={t(`Search in ${searchType === 'map' ? 'Location' : 'Patient Database'}...`)}
                inputPropsClasses={classes.searchText}
                size={size || 'small'}
                fullWidth
                variant="contained"
                onChange={handleSearch}
                value={search}
                noBorder={false}
                startAdornment={<SearchIcon style={{marginRight: 8}}/>}
                shrink
                smallerPlaceholder
                onKeyDown={handleSearchGlobal}
                enableSearch={searchType === 'global'}
                path="patientSearch"
                id="global-search-field"
                callBack={e => handleSearchGlobal(null, e)}
            />

            <GlobalSearchModal
                anchorEl={anchorEl}
                handleClose={handleCloseSearchModal}
                patients={globalPatients}
                currentWard={!onlyGlobal}
                maxResults={maxResults}
                disableMore={disableMore}
                bedManagement={bedManagement}
                autoWidth={autoWidth}
                appointments={appointments}
            />

            {!onlyGlobal &&
                <>
                    <AgyleToggleButtons
                        classNameRoot={classes.toggleRoot}
                        classNameLabel={classes.toggleLabel}
                        classNameSelected={classes.toggleSelected}
                        onChange={handleChangeType}
                        value={searchType}
                        size="small"
                        options={SEARCH_TYPE}
                        minWidth={45}
                        toggleButtonRadius={8}
                    />

                    <IconButton
                        onClick={toggle}
                        size="small"
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </>
            }
        </div>
    );
};

export default WardPanelSearch;